import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';

export interface Spelling {
  id: string;
  word: string;
  meaning: string;
  pronunciation?: string;
  synonyms: string[];
  antonyms: string[];
  example_sentence?: string;
  difficulty_level: 'easy' | 'medium' | 'hard';
  category: string;
  is_active: boolean;
  display_order: number;
  created_at: string;
  updated_at: string;
}

export interface SpellingOfTheDay {
  id: string;
  spelling_id: string;
  is_active: boolean;
  spelling?: Spelling;
}

export const useSpellingsData = () => {
  const [spellings, setSpellings] = useState<Spelling[]>([]);
  const [spellingOfTheDay, setSpellingOfTheDay] = useState<SpellingOfTheDay | null>(null);
  const [loading, setLoading] = useState(true);
  const { toast } = useToast();

  const fetchSpellings = async () => {
    try {
      const { data, error } = await supabase
        .from('spellings')
        .select('*')
        .eq('is_active', true)
        .order('created_at', { ascending: false });
      
      if (error) throw error;
      setSpellings(data || []);
    } catch (error) {
      console.error('Error fetching spellings:', error);
      toast({
        title: "Error",
        description: "Failed to fetch spellings",
        variant: "destructive",
      });
    }
  };

  const fetchSpellingOfTheDay = async () => {
    try {
      const { data, error } = await supabase
        .from('spelling_of_the_day')
        .select('*')
        .eq('is_active', true)
        .order('created_at', { ascending: false })
        .limit(1)
        .maybeSingle();
      
      if (error) throw error;
      if (!data) {
        setSpellingOfTheDay(null);
        return;
      }
      const { data: spelling, error: spellingError } = await supabase
        .from('spellings')
        .select('*')
        .eq('id', data.spelling_id)
        .maybeSingle();
      if (spellingError) throw spellingError;
      setSpellingOfTheDay({ ...data, spelling } as any);
    } catch (error) {
      console.error('Error fetching spelling of the day:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSpellings();
    fetchSpellingOfTheDay();
  }, []);

  return {
    spellings,
    spellingOfTheDay,
    loading,
    refetch: () => {
      fetchSpellings();
      fetchSpellingOfTheDay();
    }
  };
};